











































































































































import { Vue, Component, Prop } from 'vue-property-decorator';
import { Comment } from "@api/models/comments/Comment";
import { UserProfile } from "@api/models/UserProfile";
import {Element} from "@api/models/Element";

import UserIcon from "../../common/UserIcon.vue";
import StatusIcon from './StatusIcon.vue';
import CloseBtn from '@share/common/CloseBtn.vue';

import { process as processMessage } from '../../../common//MessageProcessor';
import {Modal} from "@common/Modal";
import CommentTextarea from './CommentTextarea.vue';

import { getStatus } from '../helpers/StatusHelper';

@Component({
  name: 'CommentView',
  components: { UserIcon, CloseBtn, CommentTextarea, StatusIcon },
})
export default class CommentView extends Vue {
  @Prop({ required: true }) comment!: Comment;
  @Prop({ required: true }) commentators!: Record<string, UserProfile>;
  @Prop({ required: true }) userId!: number;
  @Prop({ default: 24 }) userIconSize!: number;
  @Prop({ required: false }) elementId!: string;
  @Prop({ required: true }) alreadyEditing!: boolean;
  @Prop({ required: false }) statuses!: Element[];
  @Prop({ default: false }) readOnly!: boolean;
  @Prop({ default: false }) isLast!: boolean;

  editedMessage = '';

  get username(): string {
    if (this.profileLoaded) {
      return `${this.commentators[this.userId].firstName} ${this.commentators[this.userId].lastName}`;
    }
    return '';
  }

  get profileLoaded(): boolean {
    return !!this.userId && !!this.commentators[this.userId];
  }

  get currentLocale(): string {
    return this.$store.state.session.language || 'en';
  }

  get numAnswers(): number {
    if (!this.comment.thread) {
      return 0;
    }
    return this.comment.thread.comments.length;
  }

  get answers(): Comment[] {
    if (!this.comment.thread) {
      return [];
    }
    return this.comment.thread.comments;
  }

  get numPrevCommentsInThread(): number {
    return this.comment.thread?.previousCount || 0;
  }

  get processedText(): string {
    return this.comment.text ? processMessage(this.comment.text) : '';
  }

  get canDeleteOrEdit() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return !this.comment.isDeleted && (this.isAdmin || this.userId === this.$store.state.session.userId
        && yesterday < this.comment.createdAt.toJSDate());
  }

  get isAdmin() {
      return this.$store.getters['isAdmin'];
  }

  get cssDeletedIconStyle(): Record<string, any> {
    return {
      width: this.userIconSize + 'px',
      height: this.userIconSize + 'px',
    };
  }

  get isEditing() {
    return this.comment.isOnEdit;
  }

  // комментарий-статус
  get status() {
    if (!this.comment.text || !this.statuses) {
      return undefined;
    }
    return getStatus(this.statuses, this.comment.text);
  }

  get lineCssStyle(): Record<string, any> {
    if (this.comment.threadId) {
      return (this.$parent as CommentView).lineCssStyle;
    }
    return {
      background: this.status?.color || '#979299',
    };
  }

  toEdit() {
    if (this.alreadyEditing) {
      return;
    }

    this.$emit('startEdit', this.comment);

    this.setIsOnEdit(true);
    this.editedMessage = this.comment.text || '';
    this.focusToTextarea();
  }

  cancelEditing() {
    this.setIsOnEdit(false);
    this.$emit('cancelEdit', this.comment);
  }

  getTextAreaElement(): HTMLTextAreaElement | null {
    return this.$el.querySelector('textarea');
  }

  getTextareaComponent(): CommentTextarea | null {
    return this.$refs.inputArea as CommentTextarea;
  }

  highlightTextarea(highlight = false) {
    if (this.isEditing) {
      this.$nextTick(() => {
        this.getTextareaComponent()?.highLight('red');
      });

      this.removeSelectionFromTextarea();
      this.focusToTextarea();
    }
  }

  removeSelectionFromTextarea() {
    const textareaElement = this.getTextAreaElement();

    if (textareaElement) {
      textareaElement.value += ' ';
      textareaElement.value = textareaElement.value.slice(0, -1);
    }
  }

  focusToTextarea() {
    this.$nextTick(() => {
      this.getTextAreaElement()?.focus();
    });
  }

  setIsOnEdit(value: boolean) {
    Vue.set(this.comment, 'isOnEdit', value);
  }

  async editComment() {
    if (this.isEditing) {
      if (!this.editedMessage) {
        const deleteConfirmed = await this.deleteComment();
        if (!deleteConfirmed) {
          this.getTextAreaElement()?.focus();
        } else {
          this.setIsOnEdit(false);
        }
        return;
      }
      this.setIsOnEdit(false);
      this.$emit('editComment', this.comment, this.editedMessage);
      this.editedMessage = '';
    }
  }

  editAnswer(comment: Comment, message: string) {
    this.$emit('editComment', comment, message);
  }

  async deleteComment(withConfirmation = true) {
    if (!withConfirmation) {
      this.$emit('deleteComment', {
        comment: this.comment,
        isConfirmed: false,
      });
      return;
    }
    const modal = new Modal({
      title: this.$t("comments.delete confirmation").toString(),
      buttons: [
        {
          text: this.$t("common.yes").toString(),
          class: 'is-red',
          returnValue: true,
        },
        {
          text: this.$t("common.no").toString(),
          class: 'is-transparent-grey',
          returnValue: false,
        },
      ],
    });

    const confirmResult = await modal.showConfirm();

    if (confirmResult === true) {
      this.$emit('deleteComment', { comment: this.comment, isConfirmed: true });
    }

    return confirmResult;
  }
}
